// useDetailNavigation.ts
import { computed, type ComputedRef, onUnmounted, type Ref, unref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useReturnToStore } from '@/stores/returnTo';
import { safeStringify } from '@/utils/string';
import { eventBus } from '@/configs/eventBus';

interface UseDetailNavigationOptions {
  isSubComponent?: boolean;

  /** Overrides: can be Ref or plain value */
  uuidFromProp?: Ref<string | undefined> | string;
  editModeFromProp?: Ref<boolean | undefined> | boolean;
  isNewFromProp?: Ref<boolean | undefined> | boolean;
}

// OperatingcostinvoiceDetail.props.ts
export interface UseDetailNavigationInlineProps {
  /** UUID can be given as property to override route-derived id */
  uuid?: string;
  /** Rendered in modal context (affects toolbar visibility) */
  inModal?: boolean;
  /** Force new-mode instead of deriving from route */
  isNew?: boolean;
  /** Force edit-mode instead of deriving from route */
  editMode?: boolean;
}

export interface UseDetailNavigationPublicApi {
  id: ComputedRef<string>;
  mainComponentID: ComputedRef<string>;
  mainComponent: ComputedRef<string>;
  subComponentID: ComputedRef<string>;
  subComponent: ComputedRef<string>;
  editMode: ComputedRef<boolean>;
  isNew: ComputedRef<boolean>;
  goToList: () => void;
  goToSubcomponentList: () => void;
  toggleEditMode: () => void;
  isControlled: ComputedRef<boolean>;

  /** bind handlers to controlled events from eventBus */
  bindControlledEvents: (options?: {
    onToggleEditMode?: (next: boolean) => void;
    onGotoList?: () => void;
  }) => void;
}

export const DetailNavigationInlinePropsDefaults = {
  uuid: undefined,
  inModal: undefined,
  isNew: undefined,
  editMode: undefined,
};

export function useDetailNavigation(
  options: UseDetailNavigationOptions = {},
): UseDetailNavigationPublicApi {
  const { isSubComponent = false, uuidFromProp, editModeFromProp, isNewFromProp } = options;

  const route = useRoute();
  const router = useRouter();
  const returnToStore = useReturnToStore();

  // --- helpers ---
  const toParam = (v: unknown): string => {
    const one = Array.isArray(v) ? v[0] : v;
    return safeStringify(one) ?? '';
  };

  // --- override detection ---
  const overrideId = computed<string | undefined>(() => {
    const v = unref(uuidFromProp);
    return typeof v === 'string' && v.length > 0 ? v : undefined;
  });

  const overrideIsNew = computed<boolean | undefined>(() => {
    const v = unref(isNewFromProp);
    return typeof v === 'boolean' ? v : undefined;
  });

  const overrideEditMode = computed<boolean | undefined>(() => {
    const v = unref(editModeFromProp);
    return typeof v === 'boolean' ? v : undefined;
  });

  const usingIdOverride = computed<boolean>(() => !!overrideId.value);
  const usingEditModeOverride = computed<boolean>(() => overrideEditMode.value !== undefined);
  const usingIsNewOverride = computed<boolean>(() => overrideIsNew.value !== undefined);

  const isCreating = computed<boolean>(() => overrideIsNew.value === true);

  // --- public computed: id prefers override ---
  const id = computed<string>(() => {
    if (isCreating.value) return ''; // no ID when creating new data
    if (usingIdOverride.value) return overrideId.value!;
    return isSubComponent
      ? toParam(route.params.subComponentID)
      : toParam(route.params.mainComponentID);
  });

  const mainComponentID = computed<string>(() => {
    if (usingIdOverride.value) return id.value;
    return toParam(route.params.mainComponentID);
  });

  const mainComponent = computed<string>(() => {
    if (usingIdOverride.value || isCreating.value) return '';
    return toParam(route.params.mainComponent);
  });

  const subComponentID = computed<string>(() => {
    if (usingIdOverride.value || isCreating.value) return '';
    return toParam(route.params.subComponentID);
  });

  const subComponent = computed<string>(() => {
    if (usingIdOverride.value || isCreating.value) return '';
    return toParam(route.params.subComponent);
  });

  const editMode = computed<boolean>(() => {
    if (usingEditModeOverride.value) return overrideEditMode.value!;
    if (isCreating.value) return true;
    const modeParam = isSubComponent
      ? route.params.subComponentMode
      : route.params.mainComponentMode;
    return modeParam === 'edit' || !!modeParam;
  });

  // --- isNew prefers override, else derive from route ---
  const isNew = computed<boolean>(() => {
    if (usingIsNewOverride.value) return overrideIsNew.value!;
    if (usingIdOverride.value) return false;
    return isSubComponent
      ? route.params.subComponentMode === 'new'
      : route.params.mainComponentMode === 'new';
  });

  // --- navigation: treat "creating" as controlled too ---
  const isControlled = computed<boolean>(() => {
    return (
      isCreating.value ||
      usingIdOverride.value ||
      usingEditModeOverride.value ||
      usingIsNewOverride.value
    );
  });

  // --- navigation: short-circuit when usingOverride ---
  function toggleEditMode() {
    // External control
    if (isControlled.value) {
      // tell parent instead of routing
      eventBus.emit('toggleEditModeControlled', !editMode.value);
      return;
    }

    void router.push({
      path: route.params.subComponentID
        ? `/${mainComponent.value}/${mainComponentID.value}/${subComponent.value}/${subComponentID.value}${editMode.value ? '' : '/edit'}`
        : `/${mainComponent.value}/${mainComponentID.value}${editMode.value ? '' : '/edit'}`,
    });
  }

  function goToList() {
    // External control
    if (isControlled.value) {
      // tell parent instead of routing
      eventBus.emit('gotoListControlled');
      return;
    }

    const returnTo = returnToStore.consume();
    if (returnTo) {
      void router.push(returnTo);
      return;
    }

    if (isSubComponent) {
      void router.push(`/${mainComponent.value}/${mainComponentID.value}`);
    } else {
      void router.push(`/${mainComponent.value}`);
    }
  }

  function goToSubcomponentList() {
    if (isControlled.value) return; // external control
    eventBus.emit('closeSubcomponent', `/${mainComponent.value}/${mainComponentID.value}`);
  }

  function bindControlledEvents(options?: {
    onToggleEditMode?: (next: boolean) => void;
    onGotoList?: () => void;
  }) {
    // handlers are stable closures for on/off
    const handleToggle = (payload: unknown) => {
      const next = typeof payload === 'boolean' ? payload : false;
      if (options?.onToggleEditMode) {
        // custom handler if provided
        options.onToggleEditMode(next);
        return;
      }
      // default: use this instance's routing-based toggle
      toggleEditMode();
    };

    const handleGotoList = (_payload: unknown) => {
      if (options?.onGotoList) {
        options.onGotoList();
        return;
      }
      // default: use this instance's routing-based goToList
      goToList();
    };

    // subscribe to events that controlled children emit
    eventBus.on('toggleEditModeControlled', handleToggle);
    eventBus.on('gotoListControlled', handleGotoList);

    // cleanup automatically when component using this composable unmounts
    onUnmounted(() => {
      eventBus.off('toggleEditModeControlled', handleToggle);
      eventBus.off('gotoListControlled', handleGotoList);
    });
  }

  return {
    // state
    id,
    mainComponentID,
    mainComponent,
    subComponentID,
    subComponent,
    editMode,
    isNew,
    // actions
    goToList,
    goToSubcomponentList,
    toggleEditMode,
    // debug/flags
    isControlled,
    bindControlledEvents,
  };
}
